import { Link } from 'react-router-dom';
import { ArrowRight, TrendingUp, DollarSign, Clock, BarChart3, FileText, AlertCircle, CheckCircle, Target, Zap } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

const rcmFeatures = [
  {
    icon: FileText,
    title: 'Claims Processing',
    description: 'Accurate coding and timely claims submission to maximize first-pass acceptance rates.',
  },
  {
    icon: AlertCircle,
    title: 'Denial Management',
    description: 'Proactive denial prevention and aggressive appeals process to recover lost revenue.',
  },
  {
    icon: DollarSign,
    title: 'Patient Collections',
    description: 'Patient-friendly billing practices that improve collections while maintaining satisfaction.',
  },
  {
    icon: BarChart3,
    title: 'Analytics & Reporting',
    description: 'Real-time dashboards and insights to track performance and identify opportunities.',
  },
  {
    icon: Target,
    title: 'Payer Negotiations',
    description: 'Expert contract negotiations to secure better reimbursement rates.',
  },
  {
    icon: Zap,
    title: 'Workflow Automation',
    description: 'Technology-driven processes that reduce errors and accelerate payments.',
  },
];

const metrics = [
  { value: '32%', label: 'Average Revenue Increase', description: 'Our clients see significant revenue growth within the first year.' },
  { value: '98%', label: 'Clean Claim Rate', description: 'Industry-leading accuracy minimizes denials and rework.' },
  { value: '15', label: 'Days Faster Payment', description: 'Reduced time from service to payment posting.' },
  { value: '45%', label: 'Denial Reduction', description: 'Proactive strategies that prevent denials before they happen.' },
];

const process = [
  {
    step: '01',
    title: 'Assessment',
    description: 'We analyze your current revenue cycle to identify gaps and opportunities for improvement.',
  },
  {
    step: '02',
    title: 'Implementation',
    description: 'Our team integrates with your systems and begins optimizing processes.',
  },
  {
    step: '03',
    title: 'Optimization',
    description: 'Continuous monitoring and refinement to maximize results.',
  },
  {
    step: '04',
    title: 'Growth',
    description: 'Ongoing partnership to support your practice as it scales.',
  },
];

export const RevenueCycle = () => {
  return (
    <div data-testid="revenue-cycle-page">
      {/* Hero Section */}
      <section className="relative py-24 md:py-32 bg-navy overflow-hidden" data-testid="rcm-hero">
        <div className="absolute inset-0 opacity-20">
          <img 
            src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=1920&q=80"
            alt="Analytics dashboard with performance graphs"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="container-custom relative z-10">
          <div className="max-w-3xl">
            <span className="inline-block px-4 py-2 bg-emerald text-white font-semibold text-xs uppercase tracking-wider mb-6">
              Revenue Cycle Management
            </span>
            <h1 className="font-manrope font-bold text-4xl sm:text-5xl text-white leading-tight mb-6">
              Maximize Revenue. Minimize Hassle.
            </h1>
            <p className="text-lg text-navy-200 leading-relaxed mb-8">
              Our comprehensive revenue cycle management solutions help healthcare organizations capture every dollar they've earned while reducing administrative burden.
            </p>
            <div className="flex flex-wrap gap-4">
              <Button 
                asChild
                className="bg-emerald hover:bg-emerald-600 text-white rounded-none font-semibold tracking-wide uppercase text-xs h-12 px-8"
                data-testid="rcm-cta-primary"
              >
                <Link to="/contact">
                  Get Free Assessment
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Link>
              </Button>
              <Button 
                asChild
                variant="outline"
                className="border-2 border-white text-white hover:bg-white hover:text-navy rounded-none font-semibold tracking-wide uppercase text-xs h-12 px-8"
                data-testid="rcm-cta-secondary"
              >
                <Link to="/services">All Services</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Key Metrics - Bento Grid */}
      <section className="py-16 bg-white" data-testid="rcm-metrics">
        <div className="container-custom">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {metrics.map((metric, index) => (
              <Card 
                key={metric.label}
                className={`border-silver-light shadow-card ${index === 0 ? 'md:col-span-2 lg:col-span-1' : ''}`}
                data-testid={`rcm-metric-${metric.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <CardContent className="p-6">
                  <div className="stat-value text-4xl font-bold text-emerald mb-2">{metric.value}</div>
                  <h3 className="font-manrope font-semibold text-navy mb-2">{metric.label}</h3>
                  <p className="text-sm text-navy-600">{metric.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="section-padding bg-navy-50" data-testid="rcm-features">
        <div className="container-custom">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <span className="inline-block text-emerald font-semibold text-xs uppercase tracking-wider mb-3">
              What We Do
            </span>
            <h2 className="font-manrope font-bold text-3xl md:text-4xl text-navy mb-4">
              End-to-End Revenue Cycle Solutions
            </h2>
            <p className="text-navy-600">
              From patient registration to final payment, we optimize every step of the revenue cycle.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {rcmFeatures.map((feature) => (
              <Card 
                key={feature.title}
                className="bg-white border-silver-light shadow-card card-hover group"
                data-testid={`rcm-feature-${feature.title.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <CardContent className="p-6">
                  <div className="w-12 h-12 rounded-sm bg-navy-50 group-hover:bg-emerald flex items-center justify-center mb-4 transition-colors">
                    <feature.icon className="w-6 h-6 text-navy group-hover:text-white transition-colors" />
                  </div>
                  <h3 className="font-manrope font-semibold text-navy text-lg mb-2">{feature.title}</h3>
                  <p className="text-sm text-navy-600">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="section-padding bg-white" data-testid="rcm-process">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="inline-block text-emerald font-semibold text-xs uppercase tracking-wider mb-3">
                Our Process
              </span>
              <h2 className="font-manrope font-bold text-3xl md:text-4xl text-navy mb-6">
                How We Transform Your Revenue Cycle
              </h2>
              <p className="text-navy-600 leading-relaxed mb-8">
                Our proven methodology ensures smooth implementation and measurable results. We partner with you every step of the way.
              </p>

              <div className="space-y-6">
                {process.map((item) => (
                  <div key={item.step} className="flex gap-4" data-testid={`rcm-process-${item.step}`}>
                    <div className="w-12 h-12 rounded-sm bg-emerald flex items-center justify-center flex-shrink-0">
                      <span className="stat-value text-white font-bold">{item.step}</span>
                    </div>
                    <div>
                      <h4 className="font-manrope font-semibold text-navy mb-1">{item.title}</h4>
                      <p className="text-sm text-navy-600">{item.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="relative">
              <div className="bg-navy p-10">
                <TrendingUp className="w-16 h-16 text-emerald mb-6" />
                <h3 className="font-manrope font-bold text-2xl text-white mb-4">
                  See Results in 90 Days
                </h3>
                <p className="text-navy-200 mb-6">
                  Most clients see measurable improvements within the first quarter of partnership.
                </p>
                <ul className="space-y-3">
                  {[
                    'Increased clean claim rates',
                    'Reduced days in A/R',
                    'Higher patient collections',
                    'Improved staff productivity',
                  ].map((item) => (
                    <li key={item} className="flex items-center gap-3 text-white">
                      <CheckCircle className="w-5 h-5 text-emerald flex-shrink-0" />
                      <span className="text-sm">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Comparison */}
      <section className="section-padding bg-navy-50" data-testid="rcm-comparison">
        <div className="container-custom">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <span className="inline-block text-emerald font-semibold text-xs uppercase tracking-wider mb-3">
              The Difference
            </span>
            <h2 className="font-manrope font-bold text-3xl md:text-4xl text-navy">
              Before & After HealthCheck Medical
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <Card className="border-silver-light shadow-card" data-testid="rcm-before">
              <CardContent className="p-8">
                <h3 className="font-manrope font-semibold text-navy text-xl mb-6 flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full bg-red-500"></span>
                  Before
                </h3>
                <ul className="space-y-4">
                  {[
                    'High denial rates (15-20%)',
                    'Long days in A/R (45+ days)',
                    'Manual, error-prone processes',
                    'Limited visibility into performance',
                    'Staff overwhelmed with billing tasks',
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-3 text-navy-600">
                      <span className="w-1.5 h-1.5 rounded-full bg-red-400 mt-2 flex-shrink-0"></span>
                      {item}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            <Card className="border-emerald shadow-card bg-emerald-50" data-testid="rcm-after">
              <CardContent className="p-8">
                <h3 className="font-manrope font-semibold text-navy text-xl mb-6 flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full bg-emerald"></span>
                  After
                </h3>
                <ul className="space-y-4">
                  {[
                    'Denial rates below 5%',
                    'Days in A/R reduced to 25-30',
                    'Automated, accurate workflows',
                    'Real-time analytics dashboard',
                    'Staff focused on patient care',
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-3 text-navy-700">
                      <CheckCircle className="w-5 h-5 text-emerald flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-emerald" data-testid="rcm-cta-section">
        <div className="container-custom text-center">
          <h2 className="font-manrope font-bold text-3xl md:text-4xl text-white mb-4">
            Ready to Optimize Your Revenue Cycle?
          </h2>
          <p className="text-emerald-100 text-lg mb-8 max-w-2xl mx-auto">
            Get a free assessment of your current revenue cycle and discover how much you could be earning.
          </p>
          <Button 
            asChild
            className="bg-white text-emerald hover:bg-navy hover:text-white rounded-none font-semibold tracking-wide uppercase text-xs h-12 px-8"
            data-testid="rcm-cta-btn"
          >
            <Link to="/contact">
              Request Free Assessment
              <ArrowRight className="ml-2 w-4 h-4" />
            </Link>
          </Button>
        </div>
      </section>
    </div>
  );
};
